import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnalyseIA from './AnalyseIA';
import { lignesDuBloc } from '../utils/lignesDuDashboard';
import { BarChart3, Loader, TrendingUp } from 'lucide-react';
import ChatGPTAdGroupsTable from './ChatGPTAdGroupsTable';
import ChatGPTAdsTable from './ChatGPTAdsTable';
import DashboardCampaignHeader from './DashboardCampaignHeader';
import DashboardLoadingScreen from './DashboardLoadingScreen';
import PerformanceChart from './PerformanceChart';
import CampaignsTable from './CampaignsTable';
import NoDataMessage from './NoDataMessage';
import { PartialDataNotice } from './PartialDataNotice';
import { useClientModuleVisibility } from '../hooks/useClientModuleVisibility';
import { synchroniserCampagnes } from '../utils/synchroCampagnes';
import { compteAutorise, ensembleAutorise } from '../utils/accountMatching';
import chatgptAdsLogo from '../logos/chatgpt-ads.png';

/**
 * Dashboard ChatGPT Ads (OpenAI Ads), modèle « global ».
 *
 * La section Résultats est rendue par `PerformanceChart`, le composant partagé par tous
 * les dashboards publicitaires : cartes KPI, courbe, granularité, comparaison de période,
 * personnalisation des métriques et visibilité des modules en viennent d'un seul endroit.
 * Ce fichier n'a donc qu'une responsabilité : choisir le compte et fournir les données au
 * format attendu — `{ charts, rawData }`, celui que renvoie déjà la route backend.
 *
 * Le périmètre suit ce que l'API expose : impressions, clics, dépense, conversions, le
 * chiffre d'affaires attribué, et les ratios qui s'en déduisent. Les conversions et leur
 * valeur viennent d'un point d'entrée distinct, sans granularité temporelle — d'où la
 * ventilation quotidienne limitée aux périodes courtes (`hasDailyConversions`).
 *
 * ⚠️ Ce commentaire a longtemps annoncé « ni valeur de conversion, donc ni ROAS ni COS ».
 * C'était faux : le champ s'appelle `order_created_attributed_sales` et il manquait
 * seulement à la lecture. Les mots-clés, eux, n'existent bien pas dans les insights.
 */

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Un tableau d'entités du compte — groupes d'annonces, annonces.
 *
 * Ces tableaux ont chacun leur point d'entrée : l'API ne les livre pas avec la série
 * temporelle, et les insights d'un niveau n'exposent pas son parent. Quatre appels en
 * découlent — deux tableaux, chacun sur la période courante et sur celle de comparaison —
 * dont seuls le chemin, la clé de réponse et la période changent.
 *
 * Rend `null` sur échec, jamais `[]` : l'appelant décide alors s'il faut vider le tableau
 * ou renoncer à la comparaison, deux choses différentes.
 */
async function chargerEntites(token, chemin, cle, corps) {
  try {
    const reponse = await fetch(`${API_URL}/api/dashboard/chatgpt-ads/${chemin}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(corps)
    });
    const data = await reponse.json();
    return data.success ? (data[cle] || []) : null;
  } catch (e) {
    // Un tableau indisponible ne doit pas emporter le reste du dashboard
    return null;
  }
}

const ChatGPTAdsDashboardView = ({
  /**
   * L'analyse IA est-elle ouverte à cet utilisateur ?
   *
   * Un membre d'organisation est jugé sur son plan, un accès client sur la permission
   * `analyse-ia` accordée dans Organisation › Accès clients. La règle est calculée une fois
   * dans `App` et descendue ici : `autorise` valant `true` par défaut, ne rien passer montrait
   * le bouton à un client que le serveur refuse ensuite.
   */
  analyseAutorisee = false,
  token,
  selectedPeriod = 'LAST_30_DAYS',
  customStartDate,
  customEndDate,
  selectedClientId,
  selectedSiteId,
  userSites = [],
  preselectedAccountId,
  compareEnabled = false,
  comparisonType = 'previous_period',
  comparisonStartDate = '',
  comparisonEndDate = '',
  formatNumber,
  KPICard,
  globalConnectors = [],
  dashboardNotes = [],
  userRole = 'USER'
}) => {
  const [comptes, setComptes] = useState([]);
  const [compteSelectionne, setCompteSelectionne] = useState(null);
  const [chargementComptes, setChargementComptes] = useState(true);
  const [comptesDuClient, setComptesDuClient] = useState(null);

  const [resultats, setResultats] = useState(null);
  const [periodePrecedente, setPeriodePrecedente] = useState(null);
  // Bornes renvoyées par le backend pour la comparaison : les réutiliser évite de
  // recalculer côté front une période qui doit rester identique à celle des KPI.
  const [bornesComparaison, setBornesComparaison] = useState(null);
  const [groupes, setGroupes] = useState([]);
  const [groupesPrecedents, setGroupesPrecedents] = useState(null);
  const [annonces, setAnnonces] = useState([]);
  const [annoncesPrecedentes, setAnnoncesPrecedentes] = useState(null);
  /*
   * L'inventaire des campagnes du compte, retenu de la dernière réponse **sans filtre**.
   *
   * Le backend restreint sa réponse à la campagne filtrée : s'en servir pour alimenter la
   * liste déroulante la réduirait à un élément dès qu'un filtre est posé, et il faudrait
   * l'effacer pour pouvoir en choisir une autre.
   */
  const [campagnesConnues, setCampagnesConnues] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  // Granularité d'affichage uniquement : le regroupement se fait dans PerformanceChart,
  // sans nouvel appel réseau.
  const [granularite, setGranularite] = useState('day');
  // Le filtre traverse les deux sections : la courbe comme le tableau s'y conforment
  const [filtreCampagne, setFiltreCampagne] = useState(null);

  // Visibilité des modules pour les comptes CLIENT, comme les autres dashboards
  const clientEffectif = useMemo(
    () => (compteSelectionne ? `openai_${compteSelectionne.sourceId || compteSelectionne.id}` : null),
    [compteSelectionne]
  );
  const { isModuleVisible, toggleModuleVisibility } = useClientModuleVisibility(clientEffectif, userRole);

  // ---------------------------------------------------------------- Comptes

  useEffect(() => {
    let annule = false;

    const charger = async () => {
      setChargementComptes(true);
      try {
        const reponse = await fetch(`${API_URL}/api/library?usage=dashboards`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await reponse.json();
        if (annule) return;

        const items = data?.library?.OPENAI_ADS || [];
        // Un compte rattaché à plusieurs sites peut remonter deux fois
        setComptes(items.filter((item, i, tous) => i === tous.findIndex(t => t.id === item.id)));
      } catch (e) {
        if (!annule) setErreur('Impossible de charger les comptes ChatGPT Ads');
      } finally {
        if (!annule) setChargementComptes(false);
      }
    };

    if (token) charger();
    return () => { annule = true; };
  }, [token]);

  // Comptes rattachés aux sites du client sélectionné
  useEffect(() => {
    let annule = false;

    const charger = async () => {
      if (!selectedClientId || !token) {
        setComptesDuClient(null);
        return;
      }
      try {
        const reponse = await fetch(`${API_URL}/api/sites?clientId=${encodeURIComponent(selectedClientId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await reponse.json();
        if (annule) return;
        setComptesDuClient(ensembleAutorise((data.sites || []).flatMap(site => site.accountIds || [])));
      } catch (e) {
        // Un filtre indisponible ne doit pas masquer les comptes
        if (!annule) setComptesDuClient(null);
      }
    };

    charger();
    return () => { annule = true; };
  }, [selectedClientId, token]);

  /**
   * Le site sélectionné prime sur le client : c'est le filtre le plus précis.
   *
   * ⚠️ Le rapprochement passe par `accountMatching` et non par une comparaison à `id` et
   * `sourceId` : `/api/library` expose `id` **égal au sourceId** et range l'identifiant de
   * base sous `libraryId`, tandis qu'un rattachement récent inscrit dans `Site.accountIds`
   * ce même identifiant de base (un cuid). Les deux écritures ne se croisaient donc jamais
   * et l'écran annonçait « Aucun compte ChatGPT Ads connecté » pour un compte que l'accueil
   * chiffrait sans difficulté.
   */
  const comptesVisibles = useMemo(() => {
    if (selectedSiteId && selectedSiteId !== 'all') {
      const site = userSites.find(s => (s._id || s.id) === selectedSiteId);
      if (site?.accountIds?.length) {
        const autorises = ensembleAutorise(site.accountIds);
        return comptes.filter(c => compteAutorise(c, autorises));
      }
      // Un site sans compte rattaché ne borne rien : on retombe sur le filtre client,
      // comme le font les dashboards Google, Microsoft, Meta, Pinterest et TikTok. Rendre
      // ici tous les comptes montrerait ceux des autres clients.
    }
    if (comptesDuClient) {
      return comptes.filter(c => compteAutorise(c, comptesDuClient));
    }
    return comptes;
  }, [comptes, selectedSiteId, userSites, comptesDuClient]);

  /**
   * Filtre par campagne, appliqué à tout le dashboard : la courbe et les KPI sont
   * rechargés filtrés, les groupes d'annonces aussi. Un second clic sur la campagne déjà
   * sélectionnée retire le filtre — sans quoi il n'existerait aucun moyen d'en sortir
   * depuis le tableau.
   */
  const basculerFiltreCampagne = useCallback((campagne) => {
    setFiltreCampagne(prev => {
      if (!campagne) return null;
      const memeCampagne = prev && (prev.id ? prev.id === campagne.id : prev.name === campagne.name);
      return memeCampagne ? null : campagne;
    });
  }, []);

  /** Sélectionne un compte et publie le contexte attendu par les notes et l'URL. */
  const selectionner = useCallback((item) => {
    setCompteSelectionne(item);
    setFiltreCampagne(null);
    // Mêmes identités que le filtre ci-dessus : un site enregistré avec des cuid doit se
    // retrouver ici aussi, sans quoi la note partirait sans site ni client.
    const site = userSites.find(s => compteAutorise(item, ensembleAutorise(s.accountIds || [])));
    window.currentNotesContext = {
      platform: 'OPENAI_ADS',
      accountId: item.sourceId || item.id,
      accountName: item.name,
      siteId: site?._id || site?.id || null,
      clientId: site?.clientId || null
    };
    window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: item.id, accountName: item.name } }));
    setTimeout(() => window.updateUrlWithAccount?.(item.id), 100);
  }, [userSites]);

  // Sélection automatique : préselection d'URL, sinon compte unique
  useEffect(() => {
    if (!comptesVisibles.length) {
      setCompteSelectionne(null);
      return;
    }
    if (compteSelectionne && comptesVisibles.some(c => c.id === compteSelectionne.id)) return;

    const preselection = preselectedAccountId
      && comptesVisibles.find(c => c.id === preselectedAccountId || c.sourceId === preselectedAccountId);

    // Avec plusieurs comptes et aucune préselection, on laisse l'utilisateur choisir
    if (preselection) selectionner(preselection);
    else if (comptesVisibles.length === 1) selectionner(comptesVisibles[0]);
  }, [comptesVisibles, preselectedAccountId, compteSelectionne, selectionner]);

  // ---------------------------------------------------------------- Données

  const chargerDonnees = useCallback(async () => {
    if (!compteSelectionne || !token) return;

    setChargement(true);
    setErreur(null);
    try {
      const reponse = await fetch(`${API_URL}/api/dashboard/chatgpt-ads/timeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          sources: [compteSelectionne.id],
          period: selectedPeriod,
          customStartDate,
          customEndDate,
          // Toujours au jour : `PerformanceChart` regroupe lui-même en semaines, mois ou
          // trimestres à partir de `enrichedDailyData`. Demander la granularité au
          // backend rechargerait tout le dashboard à chaque changement de bouton, ce que
          // ne fait aucun autre dashboard.
          granularity: 'day',
          campaignFilter: filtreCampagne
        })
      });
      const data = await reponse.json();
      if (!reponse.ok || !data.success) throw new Error(data.error || 'Chargement impossible');
      setResultats(data);

      /**
       * Enregistrer les campagnes en base, comme le font les dashboards Google, Microsoft et Meta.
       *
       * Les labels et les objectifs sont attachés à une ligne `Campaign`, pas aux données de la
       * régie : sans cet appel, le panneau « Labels et objectifs » s'ouvrirait sur une table vide,
       * ce qui ressemble à une panne plutôt qu'à une absence de donnée.
       */
      synchroniserCampagnes(
        token,
        compteSelectionne?.sourceId || compteSelectionne?.id,
        'OPENAI_ADS',
        Object.values(data.rawData || {}).flatMap(compte => compte.campaigns || [])
      );
    } catch (e) {
      setErreur(e.message);
      setResultats(null);
    } finally {
      setChargement(false);
    }
  }, [compteSelectionne, token, selectedPeriod, customStartDate, customEndDate, filtreCampagne]);

  useEffect(() => { chargerDonnees(); }, [chargerDonnees]);

  useEffect(() => {
    let annule = false;

    const charger = async () => {
      const periode = resultats && Object.values(resultats.rawData || {})[0]?.period;
      if (!compareEnabled || !compteSelectionne || !periode) {
        setPeriodePrecedente(null);
        setBornesComparaison(null);
        return;
      }
      try {
        const reponse = await fetch(`${API_URL}/api/dashboard/chatgpt-ads/previous-period`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            accountId: compteSelectionne.id,
            startDate: periode.start_date,
            endDate: periode.end_date,
            comparisonType,
            comparisonStartDate,
            comparisonEndDate,
            // Le filtre doit valoir des deux côtés : comparer une campagne à tout le
            // compte produirait des variations absurdes.
            campaignFilter: filtreCampagne
          })
        });
        const data = await reponse.json();
        // PerformanceChart attend les totaux à plat, pas l'enveloppe de la réponse
        if (annule) return;
        setPeriodePrecedente(data.success ? data.metrics : null);
        setBornesComparaison(data.success ? data.period : null);
      } catch (e) {
        if (annule) return;
        setPeriodePrecedente(null);
        setBornesComparaison(null);
      }
    };

    charger();
    return () => { annule = true; };
  }, [compareEnabled, compteSelectionne, resultats, token, comparisonType, comparisonStartDate, comparisonEndDate, filtreCampagne]);

  /*
   * Groupes d'annonces et annonces, sur la période courante.
   *
   * Le filtre par campagne est appliqué côté backend, seul endroit où le rattachement
   * d'une entité à sa campagne est connu — les insights ne l'exposent à aucun niveau.
   *
   * Un échec vide le tableau. Il disparaît alors de l'écran, plutôt que de montrer les
   * chiffres de la période précédente sous une période qui a changé.
   */
  useEffect(() => {
    let annule = false;

    const charger = async () => {
      if (!compteSelectionne || !token) {
        setGroupes([]);
        setAnnonces([]);
        return;
      }

      const corps = {
        accountId: compteSelectionne.id,
        period: selectedPeriod,
        customStartDate,
        customEndDate,
        campaignFilter: filtreCampagne
      };

      const [g, a] = await Promise.all([
        chargerEntites(token, 'ad-groups', 'adGroups', corps),
        chargerEntites(token, 'ads', 'ads', corps)
      ]);
      if (annule) return;
      setGroupes(g || []);
      setAnnonces(a || []);
    };

    charger();
    return () => { annule = true; };
  }, [compteSelectionne, token, selectedPeriod, customStartDate, customEndDate, filtreCampagne]);

  /*
   * Les mêmes deux tableaux sur la période de comparaison.
   *
   * Un échec rend `null` et non `[]` : le tableau garde ses lignes et perd seulement ses
   * variations. Un tableau vide, ici, afficherait des baisses de 100 % partout.
   */
  useEffect(() => {
    let annule = false;

    const charger = async () => {
      if (!compareEnabled || !compteSelectionne || !bornesComparaison) {
        setGroupesPrecedents(null);
        setAnnoncesPrecedentes(null);
        return;
      }

      const corps = {
        accountId: compteSelectionne.id,
        period: 'CUSTOM',
        customStartDate: bornesComparaison.startDate,
        customEndDate: bornesComparaison.endDate,
        campaignFilter: filtreCampagne
      };

      const [g, a] = await Promise.all([
        chargerEntites(token, 'ad-groups', 'adGroups', corps),
        chargerEntites(token, 'ads', 'ads', corps)
      ]);
      if (annule) return;
      setGroupesPrecedents(g);
      setAnnoncesPrecedentes(a);
    };

    charger();
    return () => { annule = true; };
  }, [compareEnabled, compteSelectionne, token, bornesComparaison, filtreCampagne]);

  // ---------------------------------------------------------------- Rendu

  const connecteurPresent = globalConnectors?.some(c => c.provider === 'OPENAI_ADS') || false;
  const donneesCompte = resultats ? Object.values(resultats.rawData || {})[0] : null;
  // Un graphique est toujours présent dans la réponse, même sans point : s'y fier
  // ferait passer un compte sans diffusion pour un compte avec données, et
  // PerformanceChart afficherait une courbe plate au lieu d'un état vide franc.
  const aDesDonnees = Boolean(resultats?.charts?.[0]?.data?.length) || Boolean(donneesCompte?.campaigns?.length);

  // L'inventaire ne se met à jour que sur une réponse non filtrée, et repart de zéro au
  // changement de compte : les campagnes d'un compte n'existent pas sur l'autre.
  useEffect(() => { setCampagnesConnues([]); }, [compteSelectionne?.id]);
  useEffect(() => {
    if (filtreCampagne) return;
    if (donneesCompte?.campaigns) setCampagnesConnues(donneesCompte.campaigns);
  }, [donneesCompte?.campaigns, filtreCampagne]);

  /**
   * Le sélecteur de compte — ou `null` quand il n'y a rien à sélectionner.
   *
   * Extrait du rendu, où il vivait dans une fonction appelée sur place, pour que
   * `.dashboard-sources` ne soit monté que s'il a un contenu. Ce conteneur porte un
   * `padding-top` de 10 px : vide, il écartait l'en-tête du dashboard de 10 px de trop
   * (voir le commentaire au point de montage).
   */
  const contenuSources = (() => {
    if (chargementComptes) {
      return (
        <div className="loading-state">
          <Loader className="spin" size={16} />
          <span>Chargement des comptes ChatGPT Ads...</span>
        </div>
      );
    }

    if (comptesVisibles.length === 0) {
      return (
        <div className="no-sources">
          <div className="empty-state">
            <p>
              {connecteurPresent
                ? 'Aucun compte ChatGPT Ads connecté'
                : 'Aucun connecteur ChatGPT Ads activé'}
            </p>
            <button
              onClick={() => { window.location.hash = '#connectors'; }}
              className="btn btn-primary"
              style={{ fontSize: '16px', padding: '12px 24px' }}
            >
              + Connecter
            </button>
          </div>
        </div>
      );
    }

    // Plusieurs comptes et aucun sélectionné : on laisse choisir. Même charte que
    // les autres régies (Google Ads, TikTok Ads…) : carte centrée et liste déroulante.
    if (comptesVisibles.length > 1 && !compteSelectionne) {
      return (
        <div className="account-selection-prompt">
          <div className="account-selection-card">
            <div className="account-selection-icon">
              <img src={chatgptAdsLogo} alt="ChatGPT Ads" style={{ width: 48, height: 48, objectFit: 'contain' }} />
            </div>
            <h2>Sélectionnez un compte ChatGPT Ads</h2>
            <p>{comptesVisibles.length} comptes disponibles</p>
            <select
              className="account-selection-dropdown"
              value=""
              onChange={(e) => {
                const item = comptesVisibles.find(c => c.id === e.target.value);
                if (item) selectionner(item);
              }}
            >
              <option value="">Choisir un compte...</option>
              {[...comptesVisibles].sort((a, b) => a.name.localeCompare(b.name)).map(item => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    }

    // Un compte sélectionné : pas d'en-tête, même mise en page que Google Ads
    return null;
  })();

  /*
   * Les campagnes proposées par la liste déroulante de l'en-tête.
   *
   * `valeur` est l'objet entier, pas son nom : le backend filtre sur `campaignFilter.id`,
   * et `chargerCompte` retombe sur le nom quand l'identifiant manque. `cle` prend la même
   * précédence, pour que la sélection tienne même sur une campagne sans identifiant.
   *
   * ⚠️ **La liste vient des campagnes déjà affichées.** Un filtre actif restreint la
   * réponse du backend à cette seule campagne : la liste se réduirait donc à un élément et
   * il deviendrait impossible d'en changer sans effacer le filtre d'abord. `campagnesConnues`
   * garde le dernier inventaire complet — celui lu sans filtre.
   */
  const campagnesPourFiltre = useMemo(() => (campagnesConnues || [])
    .filter(c => c.id || c.name)
    .map(c => ({ cle: c.id || c.name, libelle: c.name || c.id, valeur: c })), [campagnesConnues]);

  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**.
   *
   * Appelée à l'envoi de la question et non à l'ouverture du panneau : les blocs arrivent en
   * plusieurs temps, et une charge figée trop tôt capture un écran incomplet.
   */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    const bruts = {
      'Campagnes': donneesCompte?.campaigns,
      "Groupes d'annonces": groupes,
      'Période précédente': periodePrecedente
    };
    const blocs = {};
    const absents = [];
    for (const [libelle, bloc] of Object.entries(bruts)) {
      const reduit = bloc ? lignesDuBloc(bloc) : null;
      if (reduit) blocs[libelle] = reduit;
      else absents.push(libelle);
    }

    return {
      dashboardData: {
        sources: [compteSelectionne?.sourceId || compteSelectionne?.id].filter(Boolean),
        ...periode,
        charts: resultats?.charts,
        composants: { blocs, absents },
        dashboardType: 'chatgpt'
      },
      dashboardType: 'chatgpt',
      accountId: compteSelectionne?.sourceId || compteSelectionne?.id || null,
      accountName: compteSelectionne?.name || null,
      ...periode
    };
  };

  return (
    <div className="view-content chatgpt-ads-dashboard">
      {/*
        L'en-tête : logo, filtre par campagne, et à droite l'analyse IA.

        **Il remplace le bouton d'analyse seul sur sa ligne**, et aligne cet écran sur celui
        de Microsoft Ads, où la barre collante porte les filtres à gauche et les actions à
        droite. Le filtre vaut pour tout le dashboard — courbe, campagnes, groupes
        d'annonces et annonces —, le backend le recevant sur chacun de ses points d'entrée.

        `AnalyseIA` rend le bouton *et* son panneau, ce dernier par un portail : le
        descendre dans l'en-tête n'a aucune incidence sur son affichage.
      */}
      {compteSelectionne && !chargement && aDesDonnees && (
        <DashboardCampaignHeader
          logo={chatgptAdsLogo}
          logoAlt="ChatGPT Ads"
          libelle="Filtrer:"
          libelleSansOptions="ChatGPT Ads"
          options={campagnesPourFiltre}
          selection={filtreCampagne ? (filtreCampagne.id || filtreCampagne.name) : null}
          onSelection={setFiltreCampagne}
          actions={(
            <AnalyseIA
              token={token}
              autorise={analyseAutorisee}
              compte={compteSelectionne}
              construireCharge={construireChargeAnalyse}
            />
          )}
        />
      )}

      {/*
        ─── La marge sous l'en-tête, et le div vide qui la doublait ───────────────────────
        *
        * `.dashboard-sources` porte `padding: 10px 0 0 0` (App.css) et ne rend **rien** dans
        * le cas courant — un compte sélectionné, le sélecteur n'a plus lieu d'être. Un div
        * de 10 px de haut restait donc entre l'en-tête et Résultats, à quoi s'ajoutaient les
        * marges des deux voisins :
        *
        *   en-tête `margin-bottom: 10px`  ·  div vide 10 px  ·  `.performance-chart-section`
        *   `margin: 15px 0`  →  10 + 10 + 15 = **35 px**
        *
        * Chez Microsoft Ads l'en-tête et Résultats sont frères directs : leurs marges
        * s'effondrent l'une dans l'autre et l'écart vaut `max(10, 15)` = **15 px**. Ne monter
        * le conteneur que lorsqu'il a quelque chose à montrer rend exactement cet écart.
        *
        * ⚠️ **Ne pas « corriger » le padding de `.dashboard-sources` dans App.css** : la
        * classe sert à tous les dashboards, et ces 10 px sont justes quand le sélecteur de
        * compte est bien là. C'est le conteneur vide qui était en trop, pas sa règle.
      */}
      {contenuSources && <div className="dashboard-sources">{contenuSources}</div>}

      {resultats?.partial && (
        <PartialDataNotice
          failedSources={resultats.failedSources}
          resolveName={(id) => comptes.find(c => c.id === id)?.name}
        />
      )}

      {erreur && (
        <div className="empty-state">
          <p>{erreur}</p>
        </div>
      )}

      {compteSelectionne && chargement && (
        <DashboardLoadingScreen
          platformName="ChatGPT Ads"
          features={[
            { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
            { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
            { icon: <img src={chatgptAdsLogo} alt="ChatGPT Ads" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />, label: 'Analyse des campagnes' }
          ]}
        />
      )}

      {compteSelectionne && !chargement && !erreur && !aDesDonnees && (
        <NoDataMessage />
      )}

      {/*
        Plus de bandeau « Dashboard filtré sur la campagne X ».

        Il disait l'état du filtre et portait le seul bouton pour le retirer, à une époque
        où le filtre se posait en cliquant une ligne du tableau des campagnes — sans lui,
        rien à l'écran n'indiquait qu'un filtre était actif. La liste déroulante de
        l'en-tête tient maintenant les deux rôles : elle affiche la campagne retenue sur
        fond bleu et porte sa croix d'effacement. C'est le modèle de Microsoft Ads, qui n'a
        pas de bandeau non plus.

        Le clic sur une ligne du tableau des campagnes continue de fonctionner, et se
        reflète dans la liste.
      */}

      {compteSelectionne && !chargement && aDesDonnees && (
        <PerformanceChart
          autoCharts={resultats}
          formatNumber={formatNumber}
          KPICard={KPICard}
          previousPeriodData={periodePrecedente}
          selectedCampaignFilter={filtreCampagne}
          selectedCampaignTypeFilter={null}
          selectedAccount={compteSelectionne}
          granularity={granularite}
          onGranularityChange={setGranularite}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          selectedPeriod={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          labelCampaignIds={null}
          notes={dashboardNotes}
          userRole={userRole}
          isClientModuleVisible={isModuleVisible}
          toggleClientModuleVisibility={toggleModuleVisibility}
          moduleId="chatgpt_results"
        />
      )}

      {compteSelectionne && !chargement && aDesDonnees
        && donneesCompte?.hasDailyConversions === false
        && donneesCompte?.metrics?.totalConversions > 0 && (
        <div style={{
          margin: '-8px 0 16px',
          padding: '10px 14px',
          fontSize: '13px',
          color: '#64748b',
          backgroundColor: '#f8fafc',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}>
          Sur une période aussi longue, les conversions ne sont connues qu'en total :
          l'API ChatGPT Ads ne les ventile pas par jour. Les cartes et le tableau sont
          justes, la courbe des conversions reste vide.
        </div>
      )}

      {compteSelectionne && !chargement && aDesDonnees && (
        <CampaignsTable
          rawData={resultats?.rawData}
          previousPeriodData={periodePrecedente}
          formatNumber={formatNumber}
          selectedCampaignFilter={filtreCampagne}
          setSelectedCampaignFilter={basculerFiltreCampagne}
          selectedCampaignTypeFilter={null}
          selectedAccount={compteSelectionne}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isModuleVisible}
          toggleClientModuleVisibility={toggleModuleVisibility}
          moduleId="chatgpt_campaigns"
          campagnesAccountId={compteSelectionne?.sourceId || compteSelectionne?.id}
          campagnesAccountType="OPENAI_ADS"
          campagnesToken={token}
        />
      )}

      {compteSelectionne && !chargement && aDesDonnees && (
        <ChatGPTAdGroupsTable
          adGroups={groupes}
          previousAdGroups={groupesPrecedents}
          formatNumber={formatNumber}
          selectedAccount={compteSelectionne}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isModuleVisible}
          toggleClientModuleVisibility={toggleModuleVisibility}
          moduleId="chatgpt_ad_groups"
        />
      )}

      {/*
        Les annonces, sous les groupes qui les portent — l'ordre de la hiérarchie du compte.

        Le tableau s'efface de lui-même s'il n'a aucune ligne : un compte sans annonce
        active, ou une campagne filtrée dont la hiérarchie n'a pas pu être lue, n'affiche
        pas un cadre vide.
      */}
      {compteSelectionne && !chargement && aDesDonnees && (
        <ChatGPTAdsTable
          ads={annonces}
          previousAds={annoncesPrecedentes}
          formatNumber={formatNumber}
          selectedAccount={compteSelectionne}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isModuleVisible}
          toggleClientModuleVisibility={toggleModuleVisibility}
          moduleId="chatgpt_ads"
        />
      )}
    </div>
  );
};

export default ChatGPTAdsDashboardView;
